import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Container, Form, Button, Alert, Row, Col, Card } from 'react-bootstrap';
import { StorageHelper } from '../utils/storageHelper';
import { CryptoHelper } from '../utils/cryptoHelper';

interface VerifyPassphraseProps {
  onVerified: () => void;
}

const VerifyPassphrase: React.FC<VerifyPassphraseProps> = ({ onVerified }) => {
  const { userEmail, encrytedMasterKey } = useAuth();
  const [passphrase, setPassphrase] = useState('');
  const [rememberPassphrase, setRememberPassphrase] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!encrytedMasterKey) {
      setError('No encrypted master key found. Please log out and log in again.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Derive key from passphrase and decrypt master key
      const data = CryptoHelper.base64ToArrayBuffer(encrytedMasterKey);
      const salt = new Uint8Array(data.slice(0, 16));
      const derivedKey = await CryptoHelper.deriveKeyFromPassword(passphrase, salt);
      
      // Verify by attempting to decrypt the master key
      await CryptoHelper.decryptMasterKey(encrytedMasterKey, derivedKey);
      
      // If decryption succeeded, store the passphrase key (secure, non-reversible)
      const exportedKey = await window.crypto.subtle.exportKey("raw", derivedKey);
      const passphraseKeyBase64 = CryptoHelper.arrayBufferToBase64(exportedKey);
      StorageHelper.setPassphrase(passphraseKeyBase64, rememberPassphrase);
      
      // Notify parent component that verification is complete
      onVerified();
    } catch (err) {
      setError('Invalid passphrase. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow">
            <Card.Header as="h3" className="text-center bg-primary text-white">
              Unlock Your Account
            </Card.Header>
            <Card.Body className="p-4">
              <Alert variant="info">
                <Alert.Heading>Welcome back, {userEmail}!</Alert.Heading>
                <p>
                  Please enter your passphrase to unlock your encrypted data.
                </p>
              </Alert>
              
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Form onSubmit={handleVerify}>
                <Form.Group className="mb-3">
                  <Form.Label>Enter Your Passphrase</Form.Label>
                  <Form.Control
                    type="password"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    placeholder="Enter your passphrase"
                    autoFocus
                    required
                  />
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="rememberPassphrase"
                    label="Remember my passphrase on this device"
                    checked={rememberPassphrase}
                    onChange={(e) => setRememberPassphrase(e.target.checked)}
                  />
                  <Form.Text className="text-muted">
                    This will keep you logged in on this device. Only select this option on personal, secure devices.
                  </Form.Text>
                </Form.Group>
                
                <div className="d-grid">
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={loading || !passphrase}
                  >
                    {loading ? 'Unlocking...' : 'Unlock'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default VerifyPassphrase;